import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import LeftNavigationBar from 'screens/Main/components/LeftNavigationBar';
import TopNavigationBar from 'screens/Main/components/TopNavigationBar';
import LoginPromptModal from 'components/LoginPromptModal';
import Footer from 'components/Footer';
import { SEOHelmet, BreadcrumbList, StructuredData } from 'components/SEO';
import { SetIcon } from 'components/SetIcon';
import { useGetPublicSetAllCardsQuery } from 'store/services/sets/publicSetsApi';
import { trackEvent } from 'utils/analytics';
import { SetCardsTable } from './components/SetCardsTable';
import './SetDetailPage.css';

// Default banner image for OG when no set icon available
const DEFAULT_BANNER =
  'https://trademagic-assets.s3.us-west-1.amazonaws.com/og-images/sets-directory.jpg';

const SetDetailPage: React.FC = () => {
  const { setCode: rawSetCode } = useParams<{ setCode: string }>();
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  // Normalize set code to lowercase
  const setCode = rawSetCode?.toLowerCase() || '';

  // State
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // URL normalization - redirect uppercase to lowercase for SEO
  useEffect(() => {
    if (rawSetCode && rawSetCode !== setCode) {
      navigate(`/sets/${setCode}`, { replace: true });
    }
  }, [rawSetCode, setCode, navigate]);

  // Fetch all cards in the set (no pagination for SEO)
  const { data, isLoading, error } = useGetPublicSetAllCardsQuery(
    setCode,
    { skip: !setCode }
  );

  // Track set view on initial load
  useEffect(() => {
    if (setCode) {
      trackEvent('set_viewed', { setCode });
    }
  }, [setCode]);

  // Mobile detection
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 768) {
        setIsMobileMenuOpen(false);
      }
    };

    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => window.removeEventListener('resize', checkIfMobile);
  }, []);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleNavigationAttempt = () => {
    if (!currentUser) {
      setShowLoginModal(true);
    }
  };

  // Format date
  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return 'Unknown';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Unknown';
    }
  };

  // Format set type
  const formatSetType = (type: string | null): string => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const set = data?.set;
  const cards = useMemo(() => data?.cards || [], [data?.cards]);
  const totalCards = data?.totalCards || set?.cardCount || 0;

  // Get first card image for OG fallback
  const firstCardImage = useMemo(() => {
    if (cards.length > 0 && cards[0].imageUris?.normal) {
      return cards[0].imageUris.normal;
    }
    return null;
  }, [cards]);

  // SEO data
  const pageTitle = set
    ? `${set.name} (${set.code.toUpperCase()}) Cards - TradeMagic`
    : 'Set Details - TradeMagic';
  const pageDescription = set
    ? `Browse all ${totalCards.toLocaleString()} cards from ${set.name}. Released ${formatDate(set.releasedAt)}. Find and trade cards on TradeMagic.`
    : 'Browse Magic: The Gathering set details on TradeMagic.';
  const canonicalUrl = `https://trademagic.gg/sets/${setCode}`;
  const ogImage = set?.iconUrl || firstCardImage || DEFAULT_BANNER;

  // CollectionPage schema for set detail
  const setCollectionSchema = set
    ? {
        '@context': 'https://schema.org',
        '@type': 'CollectionPage',
        name: `${set.name} Cards`,
        description: `Complete list of all cards from ${set.name}`,
        url: canonicalUrl,
        numberOfItems: totalCards,
        about: {
          '@type': 'Thing',
          name: set.name,
        },
      }
    : null;

  // Loading skeleton for initial load
  if (isLoading) {
    return (
      <div className="set-detail-layout">
        <div className="set-detail-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="set-detail-main">
            <TopNavigationBar
              title="Loading..."
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
            <div className="set-detail-container">
              <div className="set-detail-loading">
                <div className="set-detail-spinner" />
                <p>Loading set details...</p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  // Error state - set not found
  if (error) {
    return (
      <div className="set-detail-layout">
        <SEOHelmet
          title="Set Not Found - TradeMagic"
          description="The requested set could not be found."
          canonical={canonicalUrl}
        />
        <div className="set-detail-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />
          <div className="set-detail-main">
            <TopNavigationBar
              title="Set Not Found"
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />
            <div className="set-detail-container">
              <div className="set-detail-error">
                <h2>Set Not Found</h2>
                <p>The set "{rawSetCode}" could not be found.</p>
                <Link to="/sets" className="set-detail-back-link">
                  ← Back to all sets
                </Link>
              </div>
            </div>
          </div>
        </div>
        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />
        <Footer />
      </div>
    );
  }

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        image={ogImage}
        keywords={set ? `${set.name}, ${set.code.toUpperCase()}, MTG set, Magic cards, TradeMagic` : ''}
        type="website"
      />

      {/* BreadcrumbList Schema */}
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: 'https://trademagic.gg/' },
          { name: 'Sets', url: 'https://trademagic.gg/sets' },
          ...(set ? [{ name: set.name, url: canonicalUrl }] : []),
        ]}
      />

      {/* CollectionPage Schema */}
      {setCollectionSchema && <StructuredData data={setCollectionSchema} />}

      <div className="set-detail-layout">
        {/* Mobile Menu Overlay */}
        {isMobile && isMobileMenuOpen && (
          <div
            className="set-detail-mobile-overlay"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          />
        )}

        <div className="set-detail-content-wrapper">
          <LeftNavigationBar
            currentTab={{ path: '/sets', Component: <></> }}
            isPublicMode={!currentUser}
            onNavigationAttempt={handleNavigationAttempt}
            isAuthenticated={!!currentUser}
            isMobile={isMobile}
            isMobileMenuOpen={isMobileMenuOpen}
            onMenuClose={() => setIsMobileMenuOpen(false)}
          />

          <div className="set-detail-main">
            {/* Top Navigation Bar */}
            <TopNavigationBar
              title={set?.name || 'Set Details'}
              isMobileMenuOpen={isMobileMenuOpen}
              onToggleMobileMenu={toggleMobileMenu}
            />

            <div className="set-detail-container">
              {set && (
                <>
                  {/* Hero Section */}
                  <header className="set-detail-hero">
                    <div className="set-detail-icon-container">
                      <SetIcon
                        setIconUrl={set.iconUrl}
                        setName={set.name}
                        size="lg"
                        inverted={true}
                      />
                    </div>
                    <div className="set-detail-info">
                      <h1 className="set-detail-name">{set.name}</h1>
                      <div className="set-detail-meta">
                        <span className="set-detail-code">{set.code.toUpperCase()}</span>
                        <span className="set-detail-separator">|</span>
                        <span className="set-detail-date">{formatDate(set.releasedAt)}</span>
                        <span className="set-detail-separator">|</span>
                        <span className="set-detail-count">
                          {totalCards.toLocaleString()} cards
                        </span>
                        {set.setType && (
                          <>
                            <span className="set-detail-separator">|</span>
                            <span className="set-detail-type">{formatSetType(set.setType)}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </header>

                  {/* Cards Section */}
                  <section className="set-detail-cards-section">
                    <h2 className="set-detail-cards-title">
                      All {totalCards.toLocaleString()} Cards
                    </h2>

                    <SetCardsTable cards={cards} />
                  </section>
                </>
              )}
            </div>
          </div>
        </div>

        <LoginPromptModal
          isOpen={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          collectionOwnerUsername=""
        />

        <Footer />
      </div>
    </>
  );
};

export default SetDetailPage;
